import { Box } from "@mui/material";

import type { Course, Lesson } from "../../../api/course-service";
import type { UserProgress } from "../../../api/user-progress-service";
import type { MetroLayout, MetroSegment } from "../../../utils/metro-layout";
import { freeSpotNear, layoutMetro } from "../../../utils/metro-layout";
import type { MetroStop as MetroStopModel } from "../../../utils/metro-stops";
import { isBonus, isVerified } from "../../../utils/metro-stops";
import { GROUP_MARK_LABEL, groupMark } from "../../../utils/course-progress";

import { KataRobot } from "../../logo/KataRobot";
import { lineColor, METRO_INK, METRO_PAPER, METRO_SPUR, paleColor } from "./metro-theme";
import { MetroPlaque } from "./MetroPlaque";
import { MetroStop } from "./MetroStop";
import { MetroStopIcon } from "./MetroStopIcon";

export type MetroMapProps = {
    course: Course;
    progress: UserProgress | null;
    stops: MetroStopModel[];
    selectedLessonId?: string;
    onSelect: (lesson: Lesson) => void;
};

/** The whole course as a metro map; it keeps its width and scrolls sideways on narrow screens. */
export function MetroMap({ course, progress, stops, selectedLessonId, onSelect }: MetroMapProps) {
    const units = course.groups.map((group) => ({ id: group.id, title: group.title }));
    const layout = layoutMetro(units, course.lessons.map((lesson) => ({
        id: lesson.id, unitId: lesson.groupId, previous: lesson.previous, bonus: isBonus(lesson),
    })));
    const filled = units.filter((unit) => course.lessons.some((lesson) => lesson.groupId === unit.id));
    const current = stops.find((stop) => stop.kind === 'current');

    return (
        <Box sx={ { overflowX: 'auto', pb: 1 } }>
            <Box component="section" aria-label="Learning path" sx={ {
                position: 'relative', width: layout.width, height: layout.height, flexShrink: 0, mx: 'auto',
                borderRadius: '24px', border: '1px solid #E3E8EE', bgcolor: METRO_PAPER, overflow: 'hidden',
                backgroundImage: 'linear-gradient(#F1EBDF 1px, transparent 1px), linear-gradient(90deg, #F1EBDF 1px, transparent 1px)',
                backgroundSize: '40px 40px',
            } }>
                <MetroLines layout={ layout } travelled={ (id) => isLessonTravelled(course, progress, id) }/>

                { layout.rows.map((row) => {
                    const group = course.groups.find((candidate) => candidate.id === filled[row.unitIndex].id);
                    const lessons = course.lessons.filter((lesson) => lesson.groupId === group?.id);
                    const finished = lessons.filter((lesson) => lesson.practicable && isVerified(lesson, progress)).length;

                    return <MetroPlaque key={ row.unitIndex } number={ row.unitIndex + 1 } title={ group?.title ?? '' }
                                        color={ lineColor(row.unitIndex) } top={ row.top + 10 }
                                        side={ row.leftToRight ? 'right' : 'left' }
                                        status={ `${ GROUP_MARK_LABEL[groupMark({ title: group?.title ?? '', lessons }, progress)] } · ${ finished } of ${ lessons.filter((lesson) => lesson.practicable).length }` }/>;
                }) }

                { stops.map((stop) => {
                    const place = layout.stops[stop.lesson.id];
                    if (!place) return null;

                    return <MetroStop key={ stop.lesson.id } stop={ stop } place={ place } lineColor={ lineColor(place.unitIndex) }
                                      selected={ stop.lesson.id === selectedLessonId }
                                      onSelect={ isSelectable(stop.lesson) ? () => onSelect(stop.lesson) : undefined }/>;
                }) }

                <Finish layout={ layout }/>
                { current && layout.stops[current.lesson.id] && <YouAreHere layout={ layout } lessonId={ current.lesson.id }/> }
            </Box>
        </Box>
    );
}

/** A lesson opens when there is something behind it: enough questions to verify it, or a text to read. */
function isSelectable(lesson: Lesson): boolean {
    return lesson.practicable || lesson.hasText;
}

function isLessonTravelled(course: Course, progress: UserProgress | null, lessonId: string): boolean {
    const lesson = course.lessons.find((candidate) => candidate.id === lessonId);

    return !!lesson && isVerified(lesson, progress);
}

type MetroLinesProps = {
    layout: MetroLayout;
    travelled: (lessonId: string) => boolean;
};

/** Every line twice: a dark casing first, then the colour on top, bright where the user has been. */
function MetroLines({ layout, travelled }: MetroLinesProps) {
    const isTravelled = (segment: MetroSegment) => segment.lessons.every(travelled);
    const colorOf = (segment: MetroSegment) => {
        const color = lineColor(segment.unitIndex);
        return isTravelled(segment) ? color : paleColor(color);
    };
    // Travelled pieces go last so they are never covered by the pale ones they share a junction with
    const ordered = [ ...layout.segments ].sort((a, b) => Number(isTravelled(a)) - Number(isTravelled(b)));

    return (
        <svg width={ layout.width } height={ layout.height } style={ { position: 'absolute', left: 0, top: 0 } } aria-hidden="true">
            <path d={ layout.spurs.join(' ') } fill="none" stroke={ METRO_SPUR } strokeWidth="4" strokeLinecap="round"
                  strokeLinejoin="round" strokeDasharray="1 9"/>
            <path d={ layout.segments.map((segment) => segment.d).join(' ') } fill="none" stroke={ METRO_INK } strokeWidth="16"
                  strokeLinecap="round" strokeLinejoin="round"/>
            { ordered.map((segment, index) => (
                <path key={ index } d={ segment.d } fill="none" stroke={ colorOf(segment) } strokeWidth="9"
                      strokeLinecap="round" strokeLinejoin="round"/>
            )) }
        </svg>
    );
}

function Finish({ layout }: { layout: MetroLayout }) {
    const { x, y } = layout.finish;

    return <>
        <Box aria-hidden="true" sx={ {
            position: 'absolute', left: x - 30, top: y - 30, width: 60, height: 60, boxSizing: 'border-box', borderRadius: '50%',
            bgcolor: '#FFC940', color: METRO_INK, border: `3px solid ${ METRO_INK }`, boxShadow: `4px 4px 0 ${ METRO_INK }`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        } }>
            <MetroStopIcon kind="trophy" size={ 22 }/>
        </Box>
        <Box sx={ { position: 'absolute', left: x - 75, top: y + 38, width: 150, textAlign: 'center' } }>
            <Box sx={ { fontSize: 13, fontWeight: 800 } }>Course complete</Box>
            <Box sx={ { fontSize: 11, fontWeight: 700, color: '#637381' } }>Bonus spurs not required</Box>
        </Box>
    </>;
}

function YouAreHere({ layout, lessonId }: { layout: MetroLayout; lessonId: string }) {
    const spot = freeSpotNear(layout, layout.stops[lessonId], { w: 130, h: 48 });

    return (
        <Box aria-hidden="true" sx={ { position: 'absolute', left: spot.x, top: spot.y, display: 'flex', alignItems: 'flex-end', gap: 0.75 } }>
            <KataRobot size={ 40 }/>
            <Box component="span" sx={ {
                bgcolor: '#FFFFFF', border: `2px solid ${ METRO_INK }`, borderRadius: '10px 10px 10px 2px',
                px: 1, py: 0.5, fontSize: 12, fontWeight: 800, mb: 3.25, whiteSpace: 'nowrap',
            } }>
                You are here
            </Box>
        </Box>
    );
}
